import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Search, ChevronLeft, ChevronRight, X, Loader2 } from "lucide-react";
import { useFacetedParams } from "@/hooks/useFacetedParams";

interface FacetedListLayoutProps {
  /** Facet panels for the left sidebar. */
  filters: ReactNode;
  searchPlaceholder?: string;
  /** Total rows matching the current filters (from the API). */
  total: number;
  /** Page size used by the API. */
  limit: number;
  isLoading?: boolean;
  isFetching?: boolean;
  /** Header actions (New button, view toggles, …). */
  actions?: ReactNode;
  /** The list / table. */
  children: ReactNode;
}

/**
 * Two-column shell for server-side faceted lists: filter sidebar + searchable,
 * paginated content. Search and pagination are URL-state (`useFacetedParams`),
 * so the parent page just reads the same params to build its API query. The
 * search box is debounced before it touches the URL.
 */
export default function FacetedListLayout({
  filters,
  searchPlaceholder,
  total,
  limit,
  isLoading,
  isFetching,
  actions,
  children,
}: FacetedListLayoutProps) {
  const { t } = useTranslation();
  const { search, page, setParam, activeFilters, clearAll } = useFacetedParams();
  const [searchInput, setSearchInput] = useState(search);

  // Keep the box in sync if the URL changes externally (e.g. Clear all).
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  // Debounce typing → URL.
  useEffect(() => {
    if (searchInput === search) return;
    const t = setTimeout(() => setParam("q", searchInput || null), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      {/* Filter sidebar */}
      <aside className="lg:w-60 lg:shrink-0">
        <div className="card p-4">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
              {t("shared.facetedList.filters")}
            </h2>
            {activeFilters > 0 && (
              <button
                onClick={clearAll}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="h-3 w-3" />{" "}
                {t("shared.facetedList.clearCount", { count: activeFilters })}
              </button>
            )}
          </div>
          {filters}
        </div>
      </aside>

      {/* Main */}
      <div className="min-w-0 flex-1 space-y-4">
        {/* Toolbar */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={searchPlaceholder ?? t("shared.facetedList.searchPlaceholder")}
              className="input pl-10"
            />
            {isFetching && (
              <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-gray-300" />
            )}
          </div>
          {actions}
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="card flex h-64 items-center justify-center">
            <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
          </div>
        ) : (
          children
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <span>
            {total === 0
              ? t("common.states.noResults")
              : t("shared.facetedList.showingRange", { from, to, total })}
          </span>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setParam("page", String(page - 1))}
                disabled={page <= 1}
                className="btn-ghost btn-sm disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-2 tabular-nums">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setParam("page", String(page + 1))}
                disabled={page >= totalPages}
                className="btn-ghost btn-sm disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
